// pages/CreateTable.tsx — FINAL RTP FULLY INTEGRATED VERSION
// Real-time notify, RTP range (50-150%), warning, toast feedback

import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import useAdmin from '../hooks/useAdmin';
import { toast } from 'react-hot-toast'; // npm install react-hot-toast (if not already)

interface FormData {
  name: string;
  location: string;
  managerId: string;
  password: string;
  rtp: number;
  payoutRatio: number;
}

interface Manager {
  id: string;
  login_id?: string;
  name: string;
  email?: string;
}

interface CreatedTableSummary {
  id: string;
  name: string;
  location: string | null;
  runtimeProfile: 'pc' | 'casino';
  tablePassword: string;
  launchUrl: string;
}

const RTP_MIN_PERCENT = 50;
const RTP_MAX_PERCENT = 150;

export default function CreateTable() {
  const [managers, setManagers] = useState<Manager[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createdTable, setCreatedTable] = useState<CreatedTableSummary | null>(null);
  const { token, refreshCsrf, error, setError, socket, fetchManagers } = useAdmin();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    defaultValues: {
      rtp: 93,           // Safe default for manager
      payoutRatio: 1,
      managerId: '',
      location: '',
    },
  });

  useEffect(() => {
    if (token) {
      fetchManagers().then(data => setManagers((data || []) as any[]));
      refreshCsrf();
    }
  }, [token, refreshCsrf, fetchManagers]);

  // Real-time table created/updated listener
  useEffect(() => {
    if (!socket) return;

    socket.on('table-created', (data: { tableId: string; name: string }) => {
      toast.success(`New table created: ${data.name}`);
      // Optional: Refresh tables list if viewing manage-tables
    });

    socket.on('table-updated', () => {
      toast('Table updated in real-time');
    });

    return () => {
      socket.off('table-created');
      socket.off('table-updated');
    };
  }, [socket]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError('');

    if (data.rtp < RTP_MIN_PERCENT || data.rtp > RTP_MAX_PERCENT) {
      setError(`RTP must be between ${RTP_MIN_PERCENT}% and ${RTP_MAX_PERCENT}%`);
      toast.error(`RTP must be between ${RTP_MIN_PERCENT}% and ${RTP_MAX_PERCENT}%`);
      setLoading(false);
      return;
    }

    // Payout ratio warning
    if (data.payoutRatio < 0.5 || data.payoutRatio > 2) {
      if (!window.confirm(
        `Payout ratio ${data.payoutRatio} is unusual. Confirm this value?`
      )) {
        setLoading(false);
        return;
      }
    }

    try {
      const csrfToken = await refreshCsrf();
      const sanitizedData = {
        name: DOMPurify.sanitize(data.name.trim()),
        location: DOMPurify.sanitize(data.location.trim()),
        managerId: data.managerId || null,
        password: data.password,
        rtp: Number(data.rtp),
        payoutRatio: Number(data.payoutRatio),
      };

      const res = await fetch('/admin/create-table', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || localStorage.getItem('adminToken')}`,
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: JSON.stringify(sanitizedData),
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.error?.message || 'Failed to create table');
      }

      const location = data.location.trim();
      const launchParams = new URLSearchParams({
        tableId: result.id,
        runtime: 'pc',
        viewMode: 'personal',
      });

      setCreatedTable({
        id: result.id,
        name: data.name.trim(),
        location: location || null,
        runtimeProfile: 'pc',
        tablePassword: result.tablePassword || data.password,
        launchUrl: `/game?${launchParams.toString()}`,
      });

      toast.success(`Table created successfully! Name: ${data.name}, RTP: ${data.rtp}%`);
      reset();
    } catch (err: any) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Create New Table</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">
            ✕
          </button>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Table Name
            </label>
            <input
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="VIP Table 1"
              {...register('name', { required: 'Table name is required' })}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location (Optional)
            </label>
            <input
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Mumbai Branch"
              {...register('location')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign Manager (Optional)
            </label>
            <select
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('managerId')}
            >
              <option value="">No Manager</option>
              {managers.map(m => (
                <option key={m.id} value={m.id}>
                  {DOMPurify.sanitize(`${m.name} (${m.login_id || m.email || ""}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Table Password (Required)
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-12"
              placeholder="At least 6 characters"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              className="absolute right-3 top-10 text-gray-600 hover:text-gray-800"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              RTP (%) - Range: 50-150
            </label>
            <input
              type="number"
              step="0.01"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('rtp', {
                required: 'RTP is required',
                min: { value: RTP_MIN_PERCENT, message: `RTP must be at least ${RTP_MIN_PERCENT}%` },
                max: { value: RTP_MAX_PERCENT, message: `RTP cannot exceed ${RTP_MAX_PERCENT}%` },
                valueAsNumber: true,
              })}
            />
            {errors.rtp && <p className="mt-1 text-sm text-red-600">{errors.rtp.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payout Ratio (0-100)
            </label>
            <input
              type="number"
              step="0.01"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('payoutRatio', {
                required: 'Payout ratio is required',
                min: { value: 0, message: 'Payout ratio must be at least 0' },
                max: { value: 100, message: 'Payout ratio cannot exceed 100' },
                valueAsNumber: true,
              })}
            />
            {errors.payoutRatio && <p className="mt-1 text-sm text-red-600">{errors.payoutRatio.message}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 font-medium rounded-lg text-white transition ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Creating...
              </span>
            ) : (
              'Create Table'
            )}
          </button>
        </form>
      </div>

      {createdTable && (
        <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-950 shadow-sm">
          <h3 className="text-lg font-semibold">Table Created</h3>
          <div className="mt-3 space-y-2 text-sm">
            <div><span className="font-medium">Table Name:</span> {createdTable.name}</div>
            <div><span className="font-medium">Table ID:</span> <span className="font-mono break-all">{createdTable.id}</span></div>
            <div><span className="font-medium">Table Password:</span> <span className="font-mono break-all">{createdTable.tablePassword}</span></div>
            <div><span className="font-medium">Location:</span> {createdTable.location || 'Not set'}</div>
            <div>
              <span className="font-medium">Launch URL:</span>
              <div className="mt-1 font-mono break-all">{createdTable.launchUrl}</div>
            </div>
          </div>
        </div>
      )}

      {/* RTP Note */}
      <p className="mt-6 text-sm text-gray-500 text-center">
        RTP range is 50-150%. Payout ratio affects win distribution. Use carefully.
      </p>
    </div>
  );
}
